import { CommonModule, isPlatformBrowser, Location, NgFor } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, Input, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { IGuestsDto } from '../../../../models/IGuestsDto';
import { ILineItems } from '../../../../models/ILineItems';
import { IMeals } from '../../../../models/IMeals';
import { IThankYouPageData } from '../../../../models/IThankYouPageData';
import { ITicket } from '../../../../models/ITicket';
import { ApiService } from '../../../../services/api.service';
import { AuthService, LOGIN_TYPE_TICKETS, LOGIN_TYPE_TOPBAR } from '../../../../services/authentication/auth.service';
import { EmbeddedPaymentService } from '../../../../services/epf/embedded-payment.service';
import { ThemeService } from '../../../../services/theme.service';
import { TicketService } from '../../../../services/tickets/ticket.service';
import { EmbeddedPaymentComponent } from '../../../embedded-payment/embedded-payment.component';
import { ZeroPurchaseThankyouPageComponent } from '../../../embedded-payment/zero-purchase-thankyou-page/zero-purchase-thankyou-page.component';
import { FooterComponent } from '../../footer/footer.component';
import { HeaderComponent } from '../../header/header.component';

@Component({
  selector: 'app-guests-form',
  imports: [NgFor,
    FormsModule,
    CommonModule,
    ButtonModule,
    ReactiveFormsModule,
    SelectModule,
    HeaderComponent,
    FooterComponent,
    EmbeddedPaymentComponent,
    ProgressSpinnerModule,
    ZeroPurchaseThankyouPageComponent],
  templateUrl: './guests-form.component.html',
  styleUrl: './guests-form.component.scss'
})
export class GuestsFormComponent implements OnInit {


  public ticketService = inject(TicketService)
  public paymentService = inject(EmbeddedPaymentService)
  private readonly platformId = inject(PLATFORM_ID);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly themeService = inject(ThemeService);
  private readonly apiService = inject(ApiService);

  private auctionConfigSignal = this.apiService.auctionConfigSignal;
  ticketEventSignal = this.ticketService.ticketEventSignal;


  meals: IMeals[] = [];
  ticketsSelected: ITicket[] = [];
  discountCode?: string | null;
  comment?: string | null;
  totalGuest!: number;
  selectedAmount: number | null = null;
  form: FormGroup;
  hasGuestData: boolean = false;
  // ticketNamesPerGuest: string[] = [];
  groupedGuests: {
    ticketName: string;
    guests: FormGroup[];
  }[] = [];

  @Input() id!: number;

  // auth
  authService = inject(AuthService);
  isAuthenticated = this.oidcSecurityService.authenticated;
  authSubscription!: Subscription;
  userName: string | null = null;
  userEmail: string | null = null;


  // embedded paymnent
  token: string = '';
  visibleEmbedded: boolean = false;
  lineItems: ILineItems[] = [];
  thankYouData!: IThankYouPageData;
  isZeroPurchase: boolean = false;
  showThankYouPageModal: boolean = false;
  errorMessage: string | null = null;
  loadingEPF = this.paymentService.loadingEPF;





  constructor(private fb: FormBuilder, private router: Router, private location: Location, private renderer: Renderer2) {
    this.form = this.fb.group({
      guests: this.fb.array([]),
    });

    effect(() => {
      this.meals = this.ticketService.ticketEventSignal()?.meals ?? [];
      const eventData = this.ticketService.ticketEventSignal();
      if (eventData) {
        // console.log('event data', eventData);
        const discountCode = eventData.discountCode;
        this.discountCode = discountCode;
        this.comment = eventData.comment;

        const totalGuests = eventData.tickets.reduce((acTotal: number, t: any) => {
          const guestsIncluded = t.guestsIncluded || 0;
          const quantitySelected = t.quantitySelected || 0;
          return acTotal + (guestsIncluded * quantitySelected);
        },
          0);

        this.totalGuest = totalGuests;
        const guestsArray = this.form.get('guests') as FormArray;
        guestsArray.clear();
        this.initializeGuests(this.totalGuest);
      }
    });

  }

  private unlistenMessage?: () => void; // for cleanup

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.themeService.customizeAuctionTheme(this.id);
    }
    this.form.valueChanges.subscribe(() => {
      this.hasGuestData = this.checkGuestInfo();
    });

    //suscription
    this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
      if (isAuth) {
        this.oidcSecurityService.getUserData().subscribe(result => {
          if (result) {
            this.userName = result.name ?? '';
            this.userEmail = result.email ?? '';

            //assigning first row data
            this.updateFirstRow();

          }
        });
      } else {
        this.userName = '';
        this.userEmail = '';
        this.updateFirstRow();
      }
    });

    if (typeof window !== 'undefined') {

      // Extract the data sent by state from ticket-event
      const state = history.state as { lineItems?: ILineItems[] };
      if (state?.lineItems) {
        this.lineItems = state.lineItems;
      } else {
      }

      const stored = sessionStorage.getItem('ticketSelectedAmount');
      if (stored) {

        const ticketSelectedAmount = JSON.parse(stored);
        // console.log(ticketSelectedAmount)
        this.ticketsSelected = ticketSelectedAmount;


        if (ticketSelectedAmount) {

          const totalGuests = ticketSelectedAmount.reduce((acTotal: number, t: any) => {
            const guestsIncluded = t.guestsIncluded || 0;
            const quantitySelected = t.quantitySelected || 0;
            return acTotal + (guestsIncluded * quantitySelected);
          }, 0);

          this.totalGuest = totalGuests;
          const guestsArray = this.form.get('guests') as FormArray;
          guestsArray.clear();
          this.initializeGuests(this.totalGuest);
        }
      }
      const eventData = this.ticketService.ticketEventSignal();
      if (eventData) {
        const discountCode = eventData.discountCode;
        this.discountCode = discountCode;
        this.comment = eventData.comment;
      }
    }

    // Add window message listener for Unavailable Notification support
    this.unlistenMessage = this.renderer.listen('window', 'message', this.onHandleEpfMessage);
  }

  ngOnDestroy() {
    // clean up the window listener if it was registered
    if (this.unlistenMessage) {
      this.unlistenMessage();
      this.unlistenMessage = undefined;
    }
  }

  onHandleEpfMessage = (event: any) => {
    console.log(event.data);

    if (event.data !== null) {
      if (event.data.type === "ITEM_UNAVAILABLE") {
        this.visibleEmbedded = false; // this closes modal
        window.location.reload();
        // If we want to route somewhere else, example:
        // this.route.navigate(['/itemunavailable']);
      } else if (event.data.type === "EPF_REDIRECT_PARENT_WITH_URL" && event.data.url) {
        console.log('Redirect to Thank You page');
        this.visibleEmbedded = false; // this closes modal
        window.location.replace(event.data.url);
      }

    }
  };


  onSubmit() {
    // console.log('forvalue', this.form.value);
    // this.initializeGuests();
  }

  initializeGuests(numberGuests: number) {

    //create formgroup
    const guestsArray = this.form.get('guests') as FormArray;
    //populate formGroup array with the guests fields
    for (let i = 0; i < numberGuests; i++) {
      guestsArray.push(this.fb.group({
        name: [''],
        lastName: [''],
        email: [''],
        meal: ['']
      }));
    }

    //assigning first row data
    this.updateFirstRow();

    this.groupedGuests = [];
    const guestsControls = guestsArray.controls as FormGroup[];
    let start = 0;



    for (const ticket of this.ticketsSelected.filter(t => t.askForGuestInfo)) {
      const quantity = ticket.quantitySelected || 0; // how many tickets bougth
      const guests = ticket.guestsIncluded || 0;     // how many guests per ticket

      for (let q = 0; q < quantity; q++) {
        const guestsForThisTicketInstance = guestsControls.slice(start, start + guests); // slice making a copy from original array
        this.groupedGuests.push({
          ticketName: ticket.name,
          guests: guestsForThisTicketInstance
        });
        start += guests;
      }
    }

    // console.log(guestsArray)
  }

  updateFirstRow() {

    const guestsArray = this.form.get('guests') as FormArray;
    const firstGuest = guestsArray.at(0) as FormGroup;

    if (!guestsArray || guestsArray.length === 0) return;


    if (this.userName && this.userEmail) {
      const { name, lastName } = this.splitName(this.userName);

      if (firstGuest) {
        firstGuest.patchValue({
          name,
          lastName,
          email: this.userEmail,
          meal: ['']
        });
      }
    } else {
      firstGuest.patchValue({
        name: '',
        lastName: '',
        email: '',
        meal: ['']
      })

    }
  }

  get guests(): FormArray {
    return this.form.get('guests') as FormArray;
  }

  openLoginModal() {
    const isTestAuction = this.auctionConfigSignal()?.isTestAuction;
    if (this.ticketService.ticketEventSignal()?.isZeroPurchase) {
      this.authService.openLoginModal(LOGIN_TYPE_TOPBAR, this.embeddedPaymentLoad, 0, this.embeddedPaymentLoad);
    } else {
      this.authService.openLoginModal(
        LOGIN_TYPE_TICKETS,
        this.embeddedPaymentLoad,
        0,
        this.embeddedPaymentLoad,
        '',
        { hideGuestOption: isTestAuction }
      );
    }
  }
  goBack() {
    this.location.back();
  }


  embeddedPaymentLoad = (): void => {



    this.lineItems = this.lineItems.map(({ id, type, quantity, donationAmount }) => ({
      id,
      type,
      quantity,
      donationAmount
    }));

    this.saveGuestsForm()

    // console.log('Items', JSON.stringify(this.lineItems, null, 2));

    if (this.ticketService.ticketEventSignal()?.isZeroPurchase) {

      this.loadingEPF.set(true);

      this.paymentService.postEmbeddedPaymentSpecialPurchase(this.id!,
        this.lineItems,
        this.comment ?? '',
        this.discountCode ? this.discountCode : undefined).subscribe({
          next: (response: IThankYouPageData) => {
            this.thankYouData = response;
            this.showThankYouPageModal = true;
            this.loadingEPF.set(false);
          },
          error: (err: HttpErrorResponse) => {
            this.loadingEPF.set(false);
            if (err.status === 500) {
              return;
            } else {

              try {
                const parsed = JSON.parse(err.error);
                this.errorMessage = parsed.message;
              } catch {
                console.error('Error parsing error message');
              }
            }
          }


        });

    } else if (this.auctionConfigSignal()?.isTestAuction) {

      this.loadingEPF.set(true);

      this.paymentService.postTestPurchase(this.id!,
        this.lineItems,
        this.comment ?? '',
        undefined,
        this.discountCode ? this.discountCode : undefined).subscribe({
          next: (response: IThankYouPageData) => {
            this.thankYouData = response;
            this.showThankYouPageModal = true;
            this.loadingEPF.set(false);
          },
          error: (err: HttpErrorResponse) => {
            this.loadingEPF.set(false);
            if (err.status === 500) {
              return;
            } else {
              try {
                const parsed = JSON.parse(err.error);
                this.errorMessage = parsed.message;
              } catch {
                console.error('Error parsing error message');
              }
            }
          }
        });

    } else {
      this.loadingEPF.set(true);

      this.paymentService.
        postEmbeddedPayment(this.id!,
          this.lineItems,
          this.comment ?? '',
          undefined,
          this.discountCode ? this.discountCode : undefined).subscribe({
            next: (token) => {
              this.loadingEPF.set(false);
              this.openEmbeddedPayment(token);
            },
            error: (err: HttpErrorResponse) => {
              this.loadingEPF.set(false);
              if (err.status === 500) {
                return;
              } else {

                try {
                  const parsed = JSON.parse(err.error);
                  this.errorMessage = parsed.message;
                } catch {
                  console.error('Error parsing error message');
                }
              }
            }
          });
    }
  }

  openEmbeddedPayment(token: string) {
    this.token = token;
    this.visibleEmbedded = true;
  }

  checkGuestInfo(): boolean {
    const guests = this.form.get('guests') as FormArray;
    return guests.controls.some(control => {
      const guest = control.value;
      return !!guest.name?.trim() || !!guest.email?.trim() || !!guest.lastName?.trim() || !!guest.meal;
    });


  }

  saveGuestsForm() {

    const guestsFormArray = this.form.get('guests') as FormArray;

    const allGuests: IGuestsDto[] = guestsFormArray.value
      .filter((g: any) =>
        g.email?.trim() || g.name?.trim() || g.lastName?.trim() || g.meal
      )
      .map((g: any) => ({
        email: g.email?.trim() || '',
        firstName: g.name?.trim() || '',
        lastName: g.lastName?.trim() || '',
        mealId: g.meal !== null && g.meal !== undefined ? Number(g.meal) : undefined
      }));;  // fill the DTO with form rows that have at least one property

    let currentGuestIndex = 0;

    this.lineItems = this.lineItems.map(item => {
      const findTicket = this.ticketsSelected.find(t => t.id === item.id); // find the ticket in the received tickets array
      if (item.type === 4 && findTicket) { //get only those of type 4
        const totalGuests = (findTicket.guestsIncluded || 0) * (findTicket.quantitySelected || 0);
        const guestsForTicket: IGuestsDto[] = [];

        for (let i = 0; i < totalGuests; i++) {
          if (currentGuestIndex >= allGuests.length) {
            break; // no more valid guests available
          }
          guestsForTicket.push(allGuests[currentGuestIndex]);
          currentGuestIndex++;
        }
        if (guestsForTicket.length > 0) {
          item.guests = guestsForTicket; // only include if there is information to send
        }
      }
      return item;
    });

  }

  splitName(fullName: string): { name: string; lastName: string } {
    const parts = fullName.trim().split(/\s+/);

    let name = '';
    let lastName = '';

    switch (parts.length) {
      case 1:
        name = parts[0];
        break;

      case 2:
        [name, lastName] = parts;
        break;

      case 3:
        name = parts[0];
        lastName = parts.slice(1).join(' ');
        break;

      default:
        name = parts.slice(0, 2).join(' ');
        lastName = parts.slice(2).join(' ');
        break;
    }

    return { name, lastName };
  }
}
